import React, { useCallback, useEffect } from 'react';
import BaseDataGrid from 'common/components/base-data-grid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Typography } from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import CustomPagination from './custom-pagination';
import CampaignDataGridColumnDefinition from './campaign-data-grid-column-definition';
import {
    ICampaignType,
    IListCampaignsPayload,
    IListCampaignsSearchForm,
} from 'types/campaign';
import { useNavigate } from 'react-router-dom';
import CampaignModalDelete from './campaign-modal-delete';
import CampaignModalCopy from './campaign-modal-copy';
import {
    listCampaigns,
    copyCampaign,
    deleteCampaign,
} from 'services/campaign.service';
import { useAppSelector, useAppDispatch } from 'common/hook';
import { RootState } from 'plugins/store';
import { setCampaigns } from 'slices/campaignsSlice';
import { toast } from 'react-toastify';
import CampaignConstants from '../constants';
import CampaignCheckBoxUnchecked from './icons/campaign-check-box-uncheck';
import CampaignCheckBoxChecked from './icons/campaign-check-box-checked';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { campaignsResponseFormatter } from 'utils/campaign_helpers';
import { handleError } from 'utils/helpers';
import { setIsLoading } from 'slices/loadingSlice';

const CampaignDataGrid: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [openModalDelete, setOpenModalDelete] = React.useState(false);
    const [openModalCopy, setOpenModalCopy] = React.useState(false);
    const [actionCampaign, setActionCampaign] = React.useState<ICampaignType>();
    const campaigns = useAppSelector(
        (state: RootState) => state.campaignState.campaigns
    );

    const campaignsSearchForm = useAppSelector(
        (state: RootState) => state.campaignState.campaignsSearchForm
    );
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });
    const [rowCount, setRowCount] = React.useState(0);

    const transformListCampaignParams = (
        formState: IListCampaignsSearchForm,
        page: number
    ): IListCampaignsPayload => {
        const params = { ...formState };
        if (formState.campaignType?.includes(CampaignConstants.service.ALL)) {
            params.campaignType = [];
        }
        if (formState.status?.includes(CampaignConstants.status.ALL)) {
            params.status = [];
        }
        return {
            page: page,
            q: {
                search_with_key_cont: params.searchKeyWord,
                status_client_in: params.status?.join(','),
                service_kind_in: params.campaignType?.join(','),
                start_date: params.startDate ?? null,
                end_date: params.endDate ?? null,
            },
        };
    };

    const fetchData = useCallback(
        async (page = 1) => {
            try {
                dispatch(setIsLoading(true));
                const baseParams = transformListCampaignParams(
                    campaignsSearchForm,
                    page
                );
                const response = await listCampaigns(baseParams);
                if (!response.success) {
                    dispatch(setIsLoading(false));
                    toast.error(response.message);
                    return;
                }
                const listCampaignsFormatted = response.data.surveys.map(
                    (campaign) => campaignsResponseFormatter(campaign, t)
                );
                dispatch(setIsLoading(false));
                dispatch(setCampaigns(listCampaignsFormatted));
                setRowCount(response.data.total_items);
            } catch (error) {
                dispatch(setIsLoading(false));
                handleError(error);
            }
        },
        [campaignsSearchForm, dispatch, t]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // actions
    const onClickGridViewIconButton = (row: ICampaignType) => {
        navigate(`/admin/campaign/overview/${row.id}`);
    };

    const onClickComputerIconButton = (row: ICampaignType) => {
        navigate(`/admin/campaign/design/${row.id}`);
    };

    // const onClickIconBellButton = (row: ICampaignType) => {
    //     navigate(`/admin/campaigns/${row.id}/collect`);
    // };

    const onClickChartIconButton = (row: ICampaignType) => {
        navigate(`/admin/campaign/analytics/${row.id}`);
    };

    const onClickCopyIconButton = (row: ICampaignType) => {
        setActionCampaign(row);
        setOpenModalCopy(true);
    };

    const onClickTrashIconButton = (row: ICampaignType) => {
        setActionCampaign(row);
        setOpenModalDelete(true);
    };

    const onConfirmDeleteCampaign = async () => {
        try {
            setOpenModalDelete(false);
            const response = await deleteCampaign(actionCampaign?.id || 0);
            if (!response.success) {
                toast.error(response.message);
                return;
            }
            const newCampaigns = campaigns.filter(
                (campaign) => campaign.id !== actionCampaign?.id
            );
            dispatch(setCampaigns(newCampaigns));
            toast.success(response.message);
        } catch (error) {
            handleError(error);
        }
    };

    const onConfirmCopyCampaign = async () => {
        try {
            dispatch(setIsLoading(true));
            setOpenModalCopy(false);
            const response = await copyCampaign(actionCampaign?.id || 0);
            if (!response.success) {
                dispatch(setIsLoading(false));
                toast.error(response.message);

                return;
            }
            const newCampaigns = [...campaigns];
            newCampaigns.unshift(campaignsResponseFormatter(response.data, t));
            dispatch(setCampaigns(newCampaigns));
            dispatch(setIsLoading(false));
            toast.success(response.message);
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    };

    const columns: GridColDef[] = CampaignDataGridColumnDefinition(
        onClickGridViewIconButton,
        onClickComputerIconButton,
        onClickChartIconButton,
        onClickCopyIconButton,
        onClickTrashIconButton
    );

    function labelDisplayedRows({
        from,
        to,
        count,
    }: {
        from: number;
        to: number;
        count: number;
    }) {
        return `Xem ${from} - ${to} trên ${count !== -1 ? count : `hơn ${to}`}`;
    }
    const onCellClick = (cell: GridCellParams) => {
        if (cell.field === 'actions' || cell.field === '__check__') return;
        onClickGridViewIconButton(cell.row as ICampaignType);
    };
    return (
        <Box component="div">
            <BaseDataGrid
                onCellClick={(cell) => onCellClick(cell)}
                columns={columns}
                rows={campaigns}
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnMenu
                autoHeight
                sx={styles.dataGrid}
                rowHeight={83}
                localeText={{
                    MuiTablePagination: {
                        labelDisplayedRows,
                    },
                    footerRowSelected: (count) => (
                        <Typography component="span" fontSize={'16px'}>
                            {count} hàng đã chọn
                        </Typography>
                    ),
                    // footerRowSelected: (count) => '',
                }}
                hideFooterPagination={rowCount <= 10}
                slots={{
                    pagination: CustomPagination,
                    noRowsOverlay: () => (
                        <Box
                            component="div"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height={'100%'}
                        >
                            <Typography component="span" fontSize={'16px'}>
                                {t('page.campaign.table.noData')}
                            </Typography>
                        </Box>
                    ),
                    baseCheckbox: (props) => (
                        <Checkbox
                            {...props}
                            checkedIcon={
                                <CampaignCheckBoxChecked
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.text.primary,
                                    }}
                                />
                            }
                            icon={<CampaignCheckBoxUnchecked />}
                            indeterminateIcon={
                                <IndeterminateCheckBoxIcon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                    }}
                                />
                            }
                        />
                    ),
                }}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[]}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) => {
                    setPaginationModel(newModel);
                    fetchData(newModel.page + 1);
                }}
                rowCount={rowCount}
            />
            <CampaignModalDelete
                open={openModalDelete}
                campaignName={actionCampaign?.campaign || ''}
                handleClose={() => setOpenModalDelete(false)}
                handleDelete={onConfirmDeleteCampaign}
            />
            <CampaignModalCopy
                open={openModalCopy}
                campaignName={actionCampaign?.campaign || ''}
                handleClose={() => setOpenModalCopy(false)}
                handleCopy={onConfirmCopyCampaign}
            />
        </Box>
    );
};

const styles = createStyleSheet({
    dataGrid: {
        '--DataGrid-rowBorderColor': 'transparent',
        '.MuiDataGrid-columnHeaders': {
            height: '83px',
            justifyContent: 'center',
        },
        '.MuiDataGrid-columnHeader': {
            padding: 0,
        },
        '.MuiDataGrid-columnHeaderTitle': {
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 700,
        },
        '.MuiDataGrid-columnHeaderTitleContainer': {
            display: 'flex',
            // justifyContent: 'space-between',
            // gap: '80px',
        },
        '.MuiDataGrid-cell': {
            outline: 'none !important',
            padding: '0',
        },
        '.MuiDataGrid-footerContainer': {
            borderColor: 'transparent',
            marginBottom: '44px',
            marginRight: '54px',
        },
        '.MuiTablePagination-displayedRows': {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
        },
        border: 'none',
    },
});

export default CampaignDataGrid;
